import { isAxiosError } from 'axios';
import {
  IRoundReconConfigDataModel,
  IRoundReconDataModel,
} from '../../data-models/finance-round-recon.data-model';
import {
  IFinanceRoundDataModel,
  IInvestmentRoundSummaryDataModel,
  toInvestmentRoundSummary,
} from '../../data-models/finance-round.data-model';
import { IFundDataModel } from '../../data-models/fund.data-model';
import { ICompanyMetricsDataModel } from '../../data-models/metrics.data-model';
import { IRoundDataModel } from '../../data-models/round.data-model';
import { TransactionType } from '../../data-models/transaction-type.data-model';
import { ITransactionDataModel } from '../../data-models/transaction.data-model';
import { AtlasGlDeliveryFile } from '../../schemas/AtlasGlDeliveryFile.schema';
import { GeneralLedgerUploadData } from '../../schemas/GeneralLedgerUploadData.schema';
import { MetricsReconDataModel } from '../../schemas/MetricsRecon.schema';
import { ReconConfigDataModel } from '../../schemas/ReconConfig.schema';
import {
  TransactionAudit,
  TransactionPayload,
  TransactionResponse,
  TransactionTWithCompanies,
} from '../../types';
import { MaggieHttp } from '../MaggieHttp';
import { getContentTypeFromFilename } from './SoiAgentQueries';

export interface IUpdateTransactionPayload extends Partial<Omit<ITransactionDataModel, 'round'>> {
  companyId: number;
  id: number;
  round?: Partial<IFinanceRoundDataModel> | null;
}

export type UpdateTransactionPayload = {
  payload: IUpdateTransactionPayload;
  transactionId: number;
};

// Ledger rows get the same fund and round as their merged row, so History and Manage Round work on them.
function enrichWithFundAndRound(
  transactions: ITransactionDataModel[] | undefined,
  fundMap: Record<string, IFundDataModel>,
  roundMap: Record<string, IRoundDataModel>
) {
  transactions
    ?.flatMap((transaction) => [transaction, ...(transaction.ledgerRows ?? [])])
    .forEach((transaction) => {
      if (transaction.fundId) {
        transaction.fund = fundMap[transaction.fundId];
      }
      if (transaction.roundId && !transaction.roundNormalized) {
        transaction.roundNormalized = roundMap[transaction.roundId];
      }
    });
}

export async function fetchTransactionsByCompany2(companyId: number) {
  const response = await fetchTransactionsByCompany(companyId);
  return response.transactions;
}

export async function fetchTransactionsByCompany(
  companyId?: number | null
): Promise<TransactionTWithCompanies> {
  const response = await MaggieHttp.maggie.get<TransactionResponse>(`/transaction`, {
    params: {
      companies: [companyId],
    },
  });
  const transactions = response.data?.transactions;
  const roundMap: { [k: string]: IRoundDataModel } = {};
  const fundMap: { [k: string]: IFundDataModel } = {};
  const transTypeMap: { [k: string]: TransactionType } = {};
  if (response.data && response.data.rounds) {
    for (const round of response.data.rounds) {
      roundMap[round.id] = round;
    }
  }
  if (response.data && response.data.funds) {
    for (const fund of response.data.funds) {
      fundMap[fund.id] = fund;
    }
  }
  if (response.data && response.data.transactionTypes) {
    for (const transType of response.data.transactionTypes) {
      transTypeMap[transType.id] = transType;
    }
  }

  enrichWithFundAndRound(transactions, fundMap, roundMap);

  return { transactions: transactions || [], companies: response.data.companies };
}

export async function createTransaction(transactionPayload: TransactionPayload) {
  const { data } = await MaggieHttp.maggie.post<TransactionPayload, ITransactionDataModel>(
    '/transaction',
    transactionPayload
  );
  return data;
}

export interface IBulkTransactionRowPayload {
  companyId: number;
  transactionDate: string;
  security?: string;
  pricePerShare?: number;
  valuationAmount?: number;
  fundId?: number;
  currencyid?: number;
  fullyDilutedShares?: number;
  reason?: string;
  noOfShares?: number;
  returnOfCapitalAmount?: number;
  receivedAmount?: number;
  escrowAmount?: number;
  valuationImpact?: boolean;
  notes?: string;
}

export interface IBulkTransactionsPayload {
  transactionTypeName: string;
  dryRun: boolean;
  rows: IBulkTransactionRowPayload[];
}

export interface IBulkTransactionRowResult {
  row: number;
  errors: string[];
  warnings: string[];
}

export interface IBulkTransactionsResponse {
  valid: boolean;
  dryRun: boolean;
  rows: IBulkTransactionRowResult[];
  createdIds?: number[];
}

export async function createTransactionsBulk(payload: IBulkTransactionsPayload) {
  const { data } = await MaggieHttp.maggie.post<IBulkTransactionsPayload, IBulkTransactionsResponse>(
    '/transaction/bulk',
    payload
  );
  return data;
}

export async function fetchTransactionHistory(companyId?: number) {
  const response = await MaggieHttp.maggie.get<TransactionAudit[]>(`/transaction/${companyId}/history`);

  return response.data;
}

export async function fetchTransactions2(params?: {
  fromDate: string;
  toDate: string;
}): Promise<TransactionResponse> {
  const { data } = await MaggieHttp.maggie.get<TransactionResponse>('/transaction', {
    params,
  });
  return data;
}

export async function fetchTransactions(): Promise<TransactionTWithCompanies> {
  const response = await MaggieHttp.maggie.get<TransactionResponse>('/transaction');

  const transactions = response.data?.transactions;
  const roundMap: { [k: string]: IRoundDataModel } = {};
  const fundMap: { [k: string]: IFundDataModel } = {};
  if (response.data && response.data.rounds) {
    for (const round of response.data.rounds) {
      roundMap[round.id] = round;
    }
  }
  if (response.data && response.data.funds) {
    for (const fund of response.data.funds) {
      fundMap[fund.id] = fund;
    }
  }

  enrichWithFundAndRound(transactions, fundMap, roundMap);

  return { transactions, companies: response.data.companies };
}

export async function updateTransaction({
  payload,
  transactionId,
}: UpdateTransactionPayload): Promise<ITransactionDataModel> {
  const { data } = await MaggieHttp.maggie.put<IUpdateTransactionPayload, ITransactionDataModel>(
    `/transaction/${transactionId}`,
    payload
  );

  return data;
}

export async function deleteTransaction(id: number) {
  await MaggieHttp.maggie.delete<number>(`/transaction/${id}`);

  return id;
}

export async function fetchMetricsRecon(
  showSoiData = false,
  uploadId?: number
): Promise<MetricsReconDataModel[]> {
  try {
    const response = await MaggieHttp.maggie.get<MetricsReconDataModel[]>('/recon-value', {
      params: {
        ...(showSoiData && { showSoiData: true }),
        ...(uploadId && { uploadId }),
      },
    });

    // Sort results to put null company IDs first, then sort alphabetically by company name
    return response.data.sort((a, b) => {
      // If one has null companyId and the other doesn't, null goes first
      if (a.companyId == null && b.companyId != null) {
        return -1;
      }
      if (a.companyId != null && b.companyId == null) {
        return 1;
      }

      // If both are null or both have values, sort alphabetically by company name
      const nameA = a.soiCompanyName || a.companyName || '';
      const nameB = b.soiCompanyName || b.companyName || '';
      return nameA.localeCompare(nameB);
    });
  } catch (error) {
    console.error('[fetchMetricsRecon] Error fetching metrics reconciliation data:', error);
    throw error;
  }
}

export async function fetchReconConfig(): Promise<ReconConfigDataModel[]> {
  const { data } = await MaggieHttp.maggie.get<ReconConfigDataModel[]>('/recon-config');
  return data;
}

export async function fetchRoundsForCompany(companyId: number): Promise<IFinanceRoundDataModel[]> {
  const response = await MaggieHttp.maggie.get<IFinanceRoundDataModel[]>(`/round`, {
    params: {
      companyId,
    },
  });

  return response.data;
}

export async function fetchAllInvestmentRounds(): Promise<IFinanceRoundDataModel[]> {
  const response = await MaggieHttp.maggie.get<IFinanceRoundDataModel[]>(`/round`);
  return response.data;
}

export async function fetchInvestmentRoundSummaries(): Promise<IInvestmentRoundSummaryDataModel[]> {
  try {
    const response = await MaggieHttp.maggie.get<IInvestmentRoundSummaryDataModel[]>(`/round/summary`, {
      skipSentryStatuses: [400, 404],
    });
    return response.data;
  } catch (error) {
    // TODO(FD-6316): drop the fallback once every environment's API has GET /round/summary (FD-6305).
    // Without it the path reaches GET /round/:id, which rejects the id; the full rounds still name them.
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status === 400 || status === 404) {
      return (await fetchAllInvestmentRounds()).map(toInvestmentRoundSummary);
    }
    throw error;
  }
}

export interface IRoundUpdatePayload extends Partial<IFinanceRoundDataModel> {
  /** Emptied fields that go back to another source's value instead of staying empty. */
  fallBackFields?: string[];
}

export async function updateInvestment(investment: IRoundUpdatePayload): Promise<IFinanceRoundDataModel> {
  const response = await MaggieHttp.maggie.put<IRoundUpdatePayload, IFinanceRoundDataModel>(
    `/round/${investment.id}`,
    investment
  );

  return response.data;
}

export interface IRoundFallbackValue {
  field: string;
  value: unknown;
}

/** The fields where the person's own value shows, with what would show without it. */
export async function fetchRoundFallbackValues(roundId: number): Promise<IRoundFallbackValue[]> {
  const { data } = await MaggieHttp.maggie.get<IRoundFallbackValue[]>(`/round/${roundId}/fallback-values`, {
    skipSentryStatuses: [404],
  });
  return data;
}

export async function createInvestment(
  investment: Partial<IFinanceRoundDataModel>
): Promise<IFinanceRoundDataModel> {
  const { data } = await MaggieHttp.maggie.post<Partial<IFinanceRoundDataModel>, IFinanceRoundDataModel>(
    `/round`,
    {
      ...investment,
    }
  );
  return data;
}

export interface IDryRunDeleteResponse {
  attachedTransactions: number;
}

export async function deleteInvestment(
  investmentId: number,
  dryRun = false
): Promise<IFinanceRoundDataModel | IDryRunDeleteResponse> {
  const { data } = await MaggieHttp.maggie.delete<IFinanceRoundDataModel | IDryRunDeleteResponse>(
    `/round/${investmentId}`,
    {
      params: {
        dryRun: dryRun ? true : undefined, // api interpreta cualquier valor como true, así que necesitamos undefined para false
      },
    }
  );
  return data;
}

export interface IAumniFinancingRecord {
  id: string;
  stage: string | null;
  equity_financing_date: string | null;
  new_money_equity_classes: string | null;
  equity_financing_id: string | null;
  investmentId: number | null;
  matchExcluded: boolean;
}

export interface IAumniTransactionRecord {
  id: string;
  stage: string | null;
  date: string | null;
  equity_class: string | null;
  transaction_type: string | null;
  investmentId: number | null;
  matchExcluded: boolean;
}

export interface IAumniCompanyRecords {
  financings: IAumniFinancingRecord[];
  transactions: IAumniTransactionRecord[];
}

export interface IAumniMatchPayload {
  financingIds?: string[];
  transactionIds?: string[];
}

export async function fetchAumniRecordsForCompany(companyId: number): Promise<IAumniCompanyRecords> {
  const { data } = await MaggieHttp.maggie.get<IAumniCompanyRecords>('/aumni/company-records', {
    params: { companyId },
  });
  return data;
}

export async function matchAumniRecords(
  roundId: number,
  payload: IAumniMatchPayload
): Promise<IFinanceRoundDataModel> {
  const { data } = await MaggieHttp.maggie.post<IAumniMatchPayload, IFinanceRoundDataModel>(
    `/round/${roundId}/aumni-match`,
    payload
  );
  return data;
}

export async function unmatchAumniRecords(
  roundId: number,
  payload: IAumniMatchPayload
): Promise<IFinanceRoundDataModel> {
  const { data } = await MaggieHttp.maggie.delete<IFinanceRoundDataModel, IAumniMatchPayload>(
    `/round/${roundId}/aumni-match`,
    { data: payload }
  );
  return data;
}

export async function fetchRoundRecon(): Promise<IRoundReconDataModel[]> {
  const response = await MaggieHttp.maggie.get<IRoundReconDataModel[]>('/roundRecon');
  return response.data;
}

export async function fetchRoundReconConfig(): Promise<IRoundReconConfigDataModel> {
  const { data } = await MaggieHttp.maggie.get<IRoundReconConfigDataModel>('roundRecon/config');
  return data;
}

export type FetchDashboardDataParams = {
  companyIds?: number[];
  fundIds?: number[];
  asOfDate?: string;
};
export async function getTransactionPreview(
  transaction: TransactionPayload
): Promise<ICompanyMetricsDataModel> {
  const { data } = await MaggieHttp.maggie.post<TransactionPayload, ICompanyMetricsDataModel>(
    '/transaction/preview',
    transaction
  );
  return data;
}

export async function uploadGeneralLedger(formData: FormData, date: string) {
  const { data } = await MaggieHttp.maggie.post<FormData, IFinanceRoundDataModel>(
    '/file-upload/carta/general-ledger',
    formData,
    {
      params: {
        date,
      },
      // Large multipart GL upload + server-side parse — no client timeout.
      timeout: 0,
    }
  );
  return data;
}

/** One presigned Carta GL upload slot. `cartaGlFileId` is the cartaGlFile row id the
 *  consumer stamps a status onto — poll `getCartaGlFileStatuses` with it. */
export interface CartaGlPresignUpload {
  signedUrl: string;
  s3Key: string;
  fileName?: string;
  cartaGlFileId: number;
}

/** Request presigned S3 URLs for Carta GL files (type 'carta-gl'). The backend creates a
 *  cartaGlFile row per file (status 'uploading') and returns its id; uploading to S3 then
 *  drives the async presign→SQS→loader path. Mirrors the soi-recon presign contract. */
export async function getCartaGlPresignedUrls(
  files: { fileName: string; period?: string }[]
): Promise<CartaGlPresignUpload[]> {
  const { data } = await MaggieHttp.maggie.post<
    { files: { fileName: string; type: string; contentType: string; period?: string }[] },
    { success: boolean; uploads: CartaGlPresignUpload[] }
  >('/file-upload/presign', {
    files: files.map((file) => ({
      fileName: file.fileName,
      type: 'carta-gl',
      contentType: getContentTypeFromFilename(file.fileName),
      period: file.period,
    })),
  });
  return data.uploads;
}

export type CartaGlFileStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'error';

/** Raw per-file Carta GL processing status (id, status, errorMessage) — the source of
 *  truth for whether an upload finished. Distinct from `getGeneralLedgerUploadData`, which
 *  maps rows into the display-only admin grid shape and drops the status/id fields. */
export interface CartaGlFileStatusRow {
  id: number;
  fileName: string | null;
  status: CartaGlFileStatus;
  errorMessage: string | null;
}

export async function getCartaGlFileStatuses(): Promise<CartaGlFileStatusRow[]> {
  const { data } = await MaggieHttp.maggie.get<CartaGlFileStatusRow[]>(
    '/file-upload/carta/general-ledger/status'
  );
  return data;
}

/** Operator-supplied opts for an Atlas-pipeline ledger delivery. All optional. */
export interface AtlasUploadOpts {
  /** Rule key matching MappingRule.sourceObject in the Atlas template.
   *  Defaults to 'gl' on the backend when omitted. The GL upload form always
   *  sends 'gl' explicitly since this dialog is dedicated to general-ledger
   *  uploads — live-view stitching is exact-match on this field, so any drift
   *  (e.g. 'gl' vs 'general_ledger') silently breaks data combining. */
  sourceObject?: string;
  /** ISO YYYY-MM-DD calendar dates declaring the delivery's coverage window. */
  coverageStart?: string;
  coverageEnd?: string;
}

/**
 * FA-agnostic ledger upload routed through the Atlas pipeline (SS&C today; any
 * future manual-quarterly FA via the same endpoint, no infrastructure changes).
 * Snapshot vs incremental semantics are governed at template scope by
 * SourceClassification.deliveryMode (set by the template author); coverage
 * bounds (when supplied) drive Atlas's live-view supersedence rule. The
 * sourceObject param picks which template rules apply to the rows.
 */
export async function uploadAtlasGeneralLedger(formData: FormData, opts?: AtlasUploadOpts) {
  const params: Record<string, string> = {};
  if (opts?.sourceObject) {
    params.sourceObject = opts.sourceObject;
  }
  if (opts?.coverageStart) {
    params.coverageStart = opts.coverageStart;
  }
  if (opts?.coverageEnd) {
    params.coverageEnd = opts.coverageEnd;
  }

  const { data } = await MaggieHttp.maggie.post<
    FormData,
    { success: boolean; message: string; s3Key: string; deliveryId: number; rowCount: number }
    // Large multipart GL upload + Atlas ingest — no client timeout.
  >('/file-upload/atlas/general-ledger', formData, {
    ...(Object.keys(params).length > 0 ? { params } : {}),
    timeout: 0,
  });
  return data;
}

/** API wire-value (`'carta' | 'atlas'`) → display label shown in the Source
 *  column. "atlas" is an internal project name we don't surface to operators. */
type WireSource = 'carta' | 'atlas';
const SOURCE_LABEL: Record<WireSource, GeneralLedgerUploadData['source']> = {
  carta: 'Carta',
  atlas: 'SS&C / other FAs',
};

export type { AtlasGlDeliveryFile };

/**
 * Atlas-pipeline GL deliveries for the admin Source Files list. The server
 * (atlas.service.ts → listAdminGlDeliveryFiles) emits one row per file in each
 * non-deleted delivery with createdBy origin-prefix already stripped and
 * fileSize as a row-count string ('1,234 rows') since Atlas storage does not
 * track byte size. fundAccountantType is inferred from the deployment env.
 */
export async function fetchAtlasAdminGlDeliveryFiles(): Promise<AtlasGlDeliveryFile[]> {
  const { data } = await MaggieHttp.maggie.get<AtlasGlDeliveryFile[]>('/atlas/gl-deliveries');
  return data;
}

/**
 * Short-lived presigned S3 URL for one file in an Atlas GL delivery, keyed by
 * the delivery's file order (matches `fileIndex` on the admin grid row). The
 * server already sets Content-Disposition: attachment on the URL.
 */
export async function fetchAtlasGlFileDownloadUrl(
  deliveryId: number,
  fileIndex: number
): Promise<{ url: string }> {
  const { data } = await MaggieHttp.maggie.get<{ url: string }>(
    `/atlas/delivery/${deliveryId}/files/${fileIndex}/download-url`
  );
  return data;
}

/**
 * Merge Carta + Atlas GL admin rows for the General Ledger admin page. Both
 * upload pipelines are valid; operators see them in one list, sorted newest
 * first, with a `source` discriminator the grid renders as a badge so it's
 * obvious which pipeline a row came from. Wire `'atlas'` becomes the user-
 * facing label "SS&C / other FAs" here — the internal project name never
 * leaves this layer.
 *
 * Atlas fetch failures are tolerated (warn-log only) so a broken Atlas backend
 * does not blank-screen the page for clients on the legacy Carta path. Carta
 * fetch failures still bubble — that's the historic behavior.
 */
export async function getGeneralLedgerUploadData(): Promise<GeneralLedgerUploadData[]> {
  const [cartaResp, atlasRows] = await Promise.all([
    MaggieHttp.maggie.get<Array<Omit<GeneralLedgerUploadData, 'source'> & { source?: WireSource }>>(
      '/file-upload/carta/general-ledger/status'
    ),
    fetchAtlasAdminGlDeliveryFiles().catch((err) => {
      console.warn('Atlas GL delivery fetch failed; rendering Carta-only list', err);
      return [];
    }),
  ]);
  const carta: GeneralLedgerUploadData[] = (cartaResp.data ?? []).map((row) => ({
    ...row,
    source: SOURCE_LABEL[row.source ?? 'carta'],
  }));
  // Atlas rows carry admin-only fields (deliveryId, status, ...) the GL grid
  // doesn't render — pick only what GeneralLedgerUploadData declares.
  const atlas: GeneralLedgerUploadData[] = atlasRows.map((row) => ({
    fileName: row.fileName,
    fileFormat: row.fileFormat,
    fileSize: row.fileSize,
    createdAt: row.createdAt,
    createdBy: row.createdBy,
    startDate: row.startDate,
    sourceObject: row.sourceObject,
    source: SOURCE_LABEL[row.source ?? 'atlas'],
  }));
  return [...carta, ...atlas].sort((a, b) => {
    const aT = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
    const bT = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
    return bT - aT;
  });
}
